/**
 * ImportDataModal - Modal for importing KPI data from Excel files
 *
 * Two-step workflow:
 * 1. Download template - Get a dynamically generated Excel template with existing values
 * 2. Upload filled template - Parse the file and preview data before applying
 *
 * The modal does NOT save data directly - it calls back to the parent with parsed values
 * for the parent to handle saving via normal data value upsert flows.
 */
import { useState, useCallback, useRef } from 'react';
import { toast } from 'src/components/ui/toast';
import { usePostHog } from 'src/lib/posthog';
import {
  DownloadSimpleIcon,
  UploadSimpleIcon,
  WarningIcon,
  CheckCircleIcon,
  XIcon,
  CaretLeftIcon
} from '@phosphor-icons/react';
import Modal from 'src/components/ui/Modal';
import Button from 'src/components/ui/Button';
import Loading from 'src/components/ui/Loading';
import * as api from '../api/client';
import { useDeleteDataValue, useImportLimits } from '../api/hooks';
import type { ImportPreviewResponse, UpdateCadence } from '../api/types';
import { MONTHS, formatPeriodLabel } from '../utils/constants';
import { parseFileUploadError } from 'src/lib/api/fileUploadErrors';
import { extractErrorDetail } from 'src/lib/api/errors';

interface ImportDataModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Close the modal */
  onClose: () => void;
  /** Node ID (dashboard, group, or data card) */
  nodeId: string;
  /** Name of the node for display */
  nodeName: string;
  /** Year for the data period */
  periodYear: number;
  /** Month for the data period */
  periodMonth: number;
  /** Whether this is a bulk import (group/dashboard) or single card */
  isBulk: boolean;
  /**
   * Callback when import preview is complete.
   * Parent component should use this data to populate the editable table.
   */
  onImportPreview: (data: ImportPreviewResponse) => void;
  /**
   * Optional callback when import is complete and data has been saved.
   * Use this to close parent modals or refresh data.
   */
  onImportComplete?: () => void;
}

type Step = 'download' | 'upload' | 'preview';

/** Render a served MB limit without a pointless trailing ".0" ("10 MB", "4.8 MB"). */
function formatFileSizeLimit(megabytes: number): string {
  const rounded = Math.round(megabytes * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)} MB`;
}

export default function ImportDataModal({
  open,
  onClose,
  nodeId,
  nodeName,
  periodYear,
  periodMonth,
  isBulk,
  onImportPreview,
  onImportComplete
}: ImportDataModalProps) {
  const posthog = usePostHog();
  const [step, setStep] = useState<Step>('download');
  const [isDownloading, setIsDownloading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [previewData, setPreviewData] = useState<ImportPreviewResponse | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // Set when the file isn't a usable template, so the error offers a route back to the
  // download step instead of an instruction the user can't follow from here.
  const [uploadErrorOffersTemplate, setUploadErrorOffersTemplate] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { data: importLimits } = useImportLimits();

  // Edit state for preview table
  const [editedValues, setEditedValues] = useState<Record<string, number | null>>({});

  // Mutation for saving data
  const deleteDataValueMutation = useDeleteDataValue();
  const [isSaving, setIsSaving] = useState(false);
  // Per-row errors returned by the apply step. The flat string list is
  // rendered above the preview table; the per-cell map is used to highlight
  // individual rows (keyed by `${node_id}-${year}-${month}`).
  const [applyErrors, setApplyErrors] = useState<string[]>([]);
  const [applyErrorByCell, setApplyErrorByCell] = useState<Record<string, string>>({});

  // Event #6: Import modal opened (track when modal opens)
  // Use useEffect to capture when modal is first opened
  const [hasTrackedOpen, setHasTrackedOpen] = useState(false);

  if (open && !hasTrackedOpen) {
    posthog?.capture('assurance_import_modal_opened', {
      node_id: nodeId,
      node_name: nodeName,
      is_bulk: isBulk,
      period_year: periodYear,
      period_month: periodMonth
    });
    setHasTrackedOpen(true);
  }

  if (!open && hasTrackedOpen) {
    setHasTrackedOpen(false);
  }

  // Format period for display
  const periodDisplay = `${MONTHS[periodMonth - 1]} ${periodYear}`;

  // Helper to format period based on cadence — delegate to the shared helper
  // so quarterly labels match the rest of the app (Q FY format, fiscal year
  // starting April), instead of the calendar-quarter Math.ceil shortcut.
  const formatPeriod = (year: number, month: number | null, cadence: UpdateCadence | null, week?: number | null) =>
    formatPeriodLabel(year, month ?? 0, cadence, week);

  // Reset state when modal opens
  const handleClose = useCallback(() => {
    setStep('download');
    setPreviewData(null);
    setIsDownloading(false);
    setIsUploading(false);
    setEditedValues({});
    setUploadError(null);
    setUploadErrorOffersTemplate(false);
    setApplyErrors([]);
    setApplyErrorByCell({});
    onClose();
  }, [onClose]);

  // Download template
  const handleDownloadTemplate = async () => {
    // Event #1: Excel template download clicked
    posthog?.capture('assurance_excel_template_download_clicked', {
      node_id: nodeId,
      node_name: nodeName,
      period_year: periodYear,
      period_month: periodMonth,
      is_bulk: isBulk
    });

    setIsDownloading(true);
    try {
      const blob = await api.downloadExportTemplate(nodeId, periodYear, periodMonth);

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `kpi_import_${nodeName.replace(/\s+/g, '_')}_${periodYear}_${periodMonth}.xlsx`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Template downloaded successfully');
      // Move to upload step after successful download
      setStep('upload');
    } catch (error) {
      console.error('Failed to download template:', error);
      toast.error('Failed to download template. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  // Handle file selection
  const handleFileSelect = async (file: File, method: 'click' | 'drag_drop') => {
    const showRejection = (message: string) => {
      setUploadError(message);
      setUploadErrorOffersTemplate(false);
      toast.error(message);
    };

    // Validate file type. .xls is deliberately refused here rather than sent: the backend
    // only accepts the xlsx MIME type and openpyxl cannot read the legacy format, so a
    // .xls upload could only ever come back as a 415.
    if (file.name.toLowerCase().endsWith('.xls')) {
      showRejection('.xls files are not supported. Open the file in Excel and use Save As → Excel Workbook (.xlsx).');
      return;
    }
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      showRejection('Please upload an Excel file (.xlsx).');
      return;
    }
    if (importLimits && file.size > importLimits.max_file_size_bytes) {
      showRejection(`File is too large. Maximum file size is ${formatFileSizeLimit(importLimits.max_file_size_mb)}.`);
      return;
    }

    // Event #2: Excel file selected
    posthog?.capture('assurance_excel_file_selected', {
      node_id: nodeId,
      file_name: file.name,
      file_size: file.size,
      is_bulk: isBulk,
      method
    });

    setIsUploading(true);
    setUploadError(null);
    try {
      const preview = await api.previewImportData(nodeId, file, periodYear, periodMonth);
      setPreviewData(preview);

      // Event #3: Excel import previewed
      posthog?.capture('assurance_excel_import_previewed', {
        node_id: nodeId,
        total_cards: preview.total_cards,
        cards_with_changes: preview.cards_with_new_values,
        cards_with_errors: preview.cards_with_errors,
        historical_changes: preview.data_values.filter((v) => v.is_historical).length,
        weekly_values: preview.data_values.filter((v) => v.period_week != null).length,
        is_bulk: isBulk
      });

      setStep('preview');
    } catch (error: any) {
      console.error('Failed to parse import file:', error);

      const status = error?.response?.status || error?.parsed?.code;
      // `detail` is a string from HTTPException but an object from the global 500 handler,
      // so it has to be normalised before it reaches state — rendering the object throws
      // "Objects are not valid as a React child" and the user sees no message at all.
      const detail = extractErrorDetail(error?.response?.data?.detail);

      // Handle 413 (File Too Large) and 415 (Unsupported Media Type) errors
      if (status === 413 || status === 415) {
        const rawMessage =
          error?.parsed?.description ||
          detail ||
          (status === 415
            ? 'This file type is not supported. Please upload an Excel file (.xlsx).'
            : 'File is too large or content is too long. Please upload a smaller file.');
        const message = parseFileUploadError(rawMessage);
        setUploadError(message);
        setUploadErrorOffersTemplate(false);
        // Stay on upload step so user can try again
        setStep('upload');
      } else {
        const message = detail || 'Failed to parse file. Please ensure it matches the template format.';
        // A template-format rejection is only actionable with a fresh template, and the
        // download lives on step 1 — so surface the route rather than just the sentence.
        const isTemplateFormatError =
          status === 400 && (/template/i.test(message) || /invalid file format/i.test(message));
        setUploadError(message);
        setUploadErrorOffersTemplate(isTemplateFormatError);
        // Stay on the upload step either way so the file can be replaced in place.
        setStep('upload');
        if (!isTemplateFormatError) toast.error(message);
      }
    } finally {
      setIsUploading(false);
    }
  };

  // Handle file input change
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file, 'click');
    }
    // Reset input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle drop event
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file, 'drag_drop');
    }
  };

  // Apply the imported data - save directly to the database
  const handleApplyImport = async () => {
    if (!previewData) return;

    // Merge edited values into preview data
    const updatedDataValues = previewData.data_values.map((dv) => {
      const key = `${dv.node_id}-${dv.period_year}-${dv.period_month}-${dv.period_week}`;
      if (key in editedValues) {
        return {
          ...dv,
          value: editedValues[key]
        };
      }
      return dv;
    });

    // Separate updates/inserts from deletions. Rows the backend flagged with
    // a `validation_error` on preview are excluded from the apply step — the
    // batch endpoint will still reject them on the server, but filtering here
    // means we don't even ask, and the per-row success count is accurate.
    const valuesToUpsert = updatedDataValues.filter((dv) => dv.value !== null && !dv.validation_error);
    const valuesToDelete = updatedDataValues.filter((dv) => dv.value === null && dv.existing_data_value_id !== null);
    // Surface every row the backend flagged with a parse/validation issue —
    // including rows whose value couldn't be parsed at all (value === null
    // alongside an error). Previously these slipped past both filters and the
    // user got a green toast even though their bad cells were silently
    // dropped on the floor.
    const valuesSkipped = updatedDataValues.filter((dv) => !!dv.validation_error);

    // Count unique cards affected
    const cardsAffected = new Set([...valuesToUpsert.map((v) => v.node_id), ...valuesToDelete.map((v) => v.node_id)])
      .size;
    const hasHistoricalChanges = updatedDataValues.some((v) => v.is_historical);

    // Event #5: Excel import confirmed
    posthog?.capture('assurance_excel_import_confirmed', {
      node_id: nodeId,
      total_values: valuesToUpsert.length + valuesToDelete.length,
      cards_affected: cardsAffected,
      has_historical_changes: hasHistoricalChanges,
      weekly_values: [...valuesToUpsert, ...valuesToDelete].filter((v) => v.period_week != null).length,
      is_bulk: isBulk
    });

    setIsSaving(true);

    try {
      let successCount = 0;
      let errorCount = valuesSkipped.length;
      const rowErrors: string[] = valuesSkipped.map((dv) => `${dv.node_path || dv.node_name}: ${dv.validation_error}`);
      const cellErrors: Record<string, string> = {};
      for (const dv of valuesSkipped) {
        cellErrors[`${dv.node_id}-${dv.period_year}-${dv.period_month}-${dv.period_week}`] =
          dv.validation_error || 'Invalid value';
      }
      let deletedCount = 0;

      // Process upserts in batch — the backend now returns per-row results so
      // we surface partial success rather than treating one bad row as a
      // total failure of the upload.
      if (valuesToUpsert.length > 0) {
        try {
          const batchRequest = valuesToUpsert.map((dv) => ({
            metric_node_id: dv.node_id,
            period_year: dv.period_year,
            period_month: dv.period_month,
            period_week: dv.period_week,
            value: dv.value!
          }));

          const result = await api.batchUpsertDataValues(batchRequest);
          successCount = result.total_count;
          if (result.error_count && result.error_count > 0) {
            errorCount += result.error_count;
            if (result.results) {
              for (const row of result.results) {
                if (!row.ok && row.error) {
                  rowErrors.push(row.error);
                  cellErrors[`${row.metric_node_id}-${row.period_year}-${row.period_month}-${row.period_week}`] =
                    row.error;
                }
              }
            }
          }
        } catch (error) {
          console.error('Batch upsert failed:', error);
          errorCount += valuesToUpsert.length;
        }
      }

      // Delete null values that have existing IDs (still done individually)
      for (const dv of valuesToDelete) {
        try {
          await deleteDataValueMutation.mutateAsync({
            nodeId: dv.node_id,
            dataValueId: dv.existing_data_value_id!
          });
          deletedCount++;
        } catch (error) {
          console.error(`Failed to delete value for ${dv.node_name}:`, error);
          errorCount++;
        }
      }

      const totalChanges = successCount + deletedCount;
      if (errorCount === 0) {
        if (deletedCount > 0 && successCount > 0) {
          toast.success(
            `Successfully saved ${successCount} value${successCount !== 1 ? 's' : ''} and deleted ${deletedCount}`
          );
        } else if (deletedCount > 0) {
          toast.success(`Successfully deleted ${deletedCount} value${deletedCount !== 1 ? 's' : ''}`);
        } else {
          toast.success(`Successfully imported ${successCount} value${successCount !== 1 ? 's' : ''}`);
        }
        setApplyErrors([]);
        setApplyErrorByCell({});
        handleClose();
        onImportComplete?.();
      } else if (totalChanges > 0) {
        toast.warning(
          `Processed ${totalChanges} change${totalChanges !== 1 ? 's' : ''}, but ${errorCount} row${
            errorCount === 1 ? '' : 's'
          } failed. See the list below for details.`
        );
        setApplyErrors(rowErrors);
        setApplyErrorByCell(cellErrors);
      } else {
        toast.error(
          rowErrors.length > 0
            ? `Failed to import data — ${errorCount} row${errorCount === 1 ? '' : 's'} rejected. See the list below for details.`
            : 'Failed to import data. Please try again.'
        );
        setApplyErrors(rowErrors);
        setApplyErrorByCell(cellErrors);
      }
    } catch (error) {
      console.error('Failed to import data:', error);
      toast.error('Failed to import data. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Render step 1: Download template
  const renderDownloadStep = () => (
    <div className="space-y-4 flex-shrink-0">
      <div className="flex items-start gap-3 text-sm text-content-subtle">
        <p>
          To ensure correct import, start by downloading the Excel with all saved data in {nodeName}. The exported
          *.xlsx file will help you fill in new values correctly.
        </p>
      </div>

      <div className="bg-surface-subtle rounded-lg p-6 flex flex-col items-center gap-4">
        <Button
          variant="primary"
          icon={DownloadSimpleIcon}
          onClick={handleDownloadTemplate}
          loading={isDownloading}
          disabled={isDownloading}
        >
          Download .xlsx template
        </Button>
      </div>
    </div>
  );

  // Render step 2: Upload file
  const renderUploadStep = () => (
    <div className="space-y-4 flex-shrink-0">
      <div className="flex items-start gap-3 text-sm text-content-subtle">
        <p>
          Fill out the template with your data values, then upload it here. The file will be validated and you'll be
          able to review the changes before saving them.
        </p>
      </div>

      {isUploading ? (
        <div className="border-2 border-dashed rounded-lg p-8 flex flex-col items-center gap-4 border-border-default">
          <Loading text="Parsing file..." />
        </div>
      ) : (
        <label
          aria-label="Upload filled out template"
          className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            dragActive ? 'border-secondary-teal bg-secondary-teal/5' : 'border-border-default hover:border-border-hover'
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setDragActive(false);
          }}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            onChange={handleFileInputChange}
            className="hidden"
            aria-label="Upload file input"
          />
          <UploadSimpleIcon size={40} />
          <div>
            <p className="text-sm font-semibold text-content-default">Upload filled out template</p>
            <p className="text-xs text-content-subtle mt-1">
              {dragActive ? 'Drop file here' : 'Click or drag a file to upload'}
            </p>
            {/* State the requirements before a file is chosen, drawn from the limits the
                import endpoint itself enforces so the copy can't claim the wrong number. */}
            <p className="text-xs text-content-subtle mt-2">
              Excel .xlsx only
              {importLimits ? ` · up to ${formatFileSizeLimit(importLimits.max_file_size_mb)}` : ''}
            </p>
          </div>
        </label>
      )}

      {uploadError && (
        <div className={uploadErrorOffersTemplate ? 'rounded-lg border border-red-200 bg-red-50 p-4 space-y-3' : ''}>
          <div className="flex items-start gap-2 text-sm text-red-600">
            <WarningIcon size={20} className="flex-shrink-0" />
            <span>{uploadError}</span>
          </div>
          {uploadErrorOffersTemplate && (
            <Button
              variant="outline"
              icon={DownloadSimpleIcon}
              onClick={() => {
                setUploadError(null);
                setUploadErrorOffersTemplate(false);
                setStep('download');
              }}
            >
              Download the template
            </Button>
          )}
        </div>
      )}
    </div>
  );

  // Render step 3: Preview and apply
  const renderPreviewStep = () => {
    if (!previewData) return null;

    const valuesWithChanges = previewData.data_values; // Show all changes including deletions
    const valuesWithErrors = previewData.data_values.filter((v) => v.validation_error);
    const historicalChanges = previewData.data_values.filter((v) => v.is_historical);

    return (
      <div className="flex flex-col space-y-4 flex-1 min-h-0">
        {/* Summary stats */}
        <div className="flex gap-4 flex-shrink-0">
          <div className="flex-1 bg-surface-subtle rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-content-default">{previewData.total_cards}</p>
            <p className="text-xs text-content-subtle">Total Cards</p>
          </div>
          <div className="flex-1 bg-amber-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-amber-700">{previewData.cards_with_new_values}</p>
            <p className="text-xs text-amber-700">Changes</p>
          </div>
          {historicalChanges.length > 0 && (
            <div className="flex-1 bg-orange-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-orange-700">{historicalChanges.length}</p>
              <p className="text-xs text-orange-700">Historical</p>
            </div>
          )}
          {previewData.cards_with_errors > 0 && (
            <div className="flex-1 bg-red-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-red-700">{previewData.cards_with_errors}</p>
              <p className="text-xs text-red-700">Errors</p>
            </div>
          )}
        </div>

        {/* Historical warning */}
        {historicalChanges.length > 0 && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex-shrink-0">
            <div className="flex items-start gap-2">
              <WarningIcon size={20} className="text-orange-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-orange-800">
                  You are modifying {historicalChanges.length} historical value{historicalChanges.length > 1 ? 's' : ''}{' '}
                  from past periods
                </p>
                <p className="text-xs text-orange-700 mt-1">
                  Please review these changes carefully as they will update previously saved data.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Apply-step errors: shown after the user clicks Apply and the
            backend rejects rows. Full list, scrollable; no cap. */}
        {applyErrors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex-shrink-0">
            <div className="flex items-start gap-2">
              <WarningIcon size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-red-800">
                  {applyErrors.length} row{applyErrors.length === 1 ? '' : 's'} rejected
                </p>
                <ul className="text-xs text-red-700 mt-1 list-disc list-inside max-h-40 overflow-y-auto">
                  {applyErrors.map((message, idx) => (
                    <li key={idx}>{message}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Errors warning */}
        {valuesWithErrors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex-shrink-0">
            <div className="flex items-start gap-2">
              <WarningIcon size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-red-800">
                  {valuesWithErrors.length} value{valuesWithErrors.length > 1 ? 's have' : ' has'} validation errors
                </p>
                <ul className="text-xs text-red-700 mt-1 list-disc list-inside max-h-40 overflow-y-auto">
                  {valuesWithErrors.map((v, i) => (
                    <li key={i}>
                      {v.node_path || v.node_name} (
                      {formatPeriod(v.period_year, v.period_month, v.update_cadence, v.period_week)}):{' '}
                      {v.validation_error}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Preview table */}
        <div className="flex-1 flex flex-col border border-border-default rounded-lg min-h-0">
          <table className="w-full text-sm table-fixed">
            <thead>
              <tr className="bg-surface-subtle border-b border-border-default">
                <th className="text-left px-3 py-2 font-semibold text-content-subtle" style={{ width: '60%' }}>
                  Card
                </th>
                <th className="text-center px-3 py-2 font-semibold text-content-subtle" style={{ width: '8%' }}>
                  Period
                </th>
                <th className="text-center px-3 py-2 font-semibold text-content-subtle" style={{ width: '15%' }}>
                  Current
                </th>
                <th className="text-center px-3 py-2 font-semibold text-content-subtle" style={{ width: '15%' }}>
                  New
                </th>
                <th className="text-center px-2 py-2 font-semibold text-content-subtle" style={{ width: '5%' }}></th>
              </tr>
            </thead>
          </table>
          <div className="flex-1 overflow-y-auto">
            <table className="w-full text-sm table-fixed">
              <colgroup>
                <col style={{ width: '60%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '5%' }} />
              </colgroup>
              <tbody>
                {valuesWithChanges.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-3 py-8 text-center text-content-subtle">
                      No changes found in the uploaded file.
                    </td>
                  </tr>
                ) : (
                  valuesWithChanges.map((row, index) => {
                    const isHistorical = row.is_historical;
                    const key = `${row.node_id}-${row.period_year}-${row.period_month}-${row.period_week}`;
                    const displayValue = key in editedValues ? editedValues[key] : row.value;
                    const hasBeenEdited = key in editedValues;
                    const isDeletion = displayValue === null && row.existing_value !== null;

                    // Validate edited value
                    let editError: string | null = null;
                    if (hasBeenEdited && displayValue !== null) {
                      if (row.data_value_type === 'percentage' && (displayValue < 0 || displayValue > 100)) {
                        editError = 'Percentage must be between 0 and 100';
                      }
                    }

                    // The apply-step error map is keyed by the same identity
                    // triple the backend returns in DataValueBatchUpsertResultItem.
                    const applyError = applyErrorByCell[key];
                    const finalError = editError || applyError || (!hasBeenEdited ? row.validation_error : null);

                    return (
                      <tr
                        key={`${row.node_id}-${row.period_year}-${row.period_month}-${row.period_week}-${index}`}
                        className={`border-b border-b-default last:border-b-0 ${
                          finalError
                            ? 'bg-red-50/50'
                            : isDeletion
                              ? 'bg-red-50/30'
                              : isHistorical
                                ? 'bg-orange-50/30'
                                : 'bg-white'
                        }`}
                      >
                        <td className="px-3 py-2">
                          <p className="font-medium text-content-default truncate">{row.node_name}</p>
                          {isBulk && row.node_path !== row.node_name && (
                            <p className="text-xs text-content-subtlest truncate">{row.node_path}</p>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <span className={isHistorical ? 'text-orange-700 font-medium' : 'text-content-default'}>
                            {formatPeriod(row.period_year, row.period_month, row.update_cadence, row.period_week)}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right text-content-subtle">
                          {row.node_type === 'status_card' ? (
                            row.existing_value === 1 ? (
                              <span>Yes</span>
                            ) : row.existing_value === 0 ? (
                              <span>No</span>
                            ) : (
                              <span className="text-content-subtlest">—</span>
                            )
                          ) : row.existing_value !== null ? (
                            <span>
                              {row.existing_value}
                              {row.data_value_type === 'percentage' && '%'}
                            </span>
                          ) : (
                            <span className="text-content-subtlest">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {isDeletion ? (
                              <span className="w-full px-2 py-1 text-right text-red-600 font-medium italic">
                                Delete
                              </span>
                            ) : row.node_type === 'status_card' ? (
                              <span
                                className={`px-2 py-1 font-medium ${
                                  finalError
                                    ? 'text-red-700'
                                    : hasBeenEdited
                                      ? 'text-green-700'
                                      : 'text-content-default'
                                }`}
                              >
                                {displayValue === 1 ? 'Yes' : displayValue === 0 ? 'No' : '—'}
                              </span>
                            ) : (
                              <input
                                type="number"
                                className={`w-full px-2 py-1 text-right border rounded [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                                  finalError
                                    ? 'border-red-300 bg-red-50 text-red-700'
                                    : hasBeenEdited
                                      ? 'border-green-300 bg-green-50 text-content-default'
                                      : 'border-border-default text-content-default'
                                }`}
                                value={displayValue ?? ''}
                                onChange={(e) => {
                                  const val = e.target.value === '' ? null : parseFloat(e.target.value);
                                  const prevEditedCount = Object.keys(editedValues).length;
                                  setEditedValues((prev) => ({
                                    ...prev,
                                    [key]: val
                                  }));

                                  // Event #4: Excel import value edited (only fire once when first edit is made)
                                  if (prevEditedCount === 0) {
                                    posthog?.capture('assurance_excel_import_value_edited', {
                                      node_id: nodeId,
                                      is_bulk: isBulk
                                    });
                                  }
                                }}
                                step={row.data_value_type === 'percentage' ? '0.01' : '0.01'}
                                placeholder="Enter value"
                              />
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          {finalError ? (
                            <span title={finalError}>
                              <WarningIcon size={16} className="text-red-600" />
                            </span>
                          ) : isDeletion ? (
                            <span title="This value will be deleted">
                              <XIcon size={16} className="text-red-600" weight="bold" />
                            </span>
                          ) : hasBeenEdited ? (
                            <span title="Value edited">
                              <CheckCircleIcon size={16} className="text-green-600" />
                            </span>
                          ) : isHistorical ? (
                            <span title="Historical data change">
                              <WarningIcon size={16} className="text-orange-600" />
                            </span>
                          ) : (
                            <CheckCircleIcon size={16} className="text-green-600" />
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Determine modal title based on step
  const getTitle = () => {
    if (step === 'preview') {
      return 'Review & Edit Import Data';
    }
    return 'Import Data';
  };

  // Check for validation errors in edited values and original data
  const hasValidationErrors = previewData
    ? // Check for errors in original data that haven't been edited
      previewData.data_values.some((row) => {
        const key = `${row.node_id}-${row.period_year}-${row.period_month}-${row.period_week}`;
        const hasBeenEdited = key in editedValues;
        // If not edited and has validation error, that's a problem
        if (!hasBeenEdited && row.validation_error) {
          return true;
        }
        return false;
      }) ||
      // Check for errors in edited values
      previewData.data_values.some((row) => {
        const key = `${row.node_id}-${row.period_year}-${row.period_month}-${row.period_week}`;
        if (key in editedValues) {
          const val = editedValues[key];
          if (val !== null && row.data_value_type === 'percentage' && (val < 0 || val > 100)) {
            return true;
          }
        }
        return false;
      })
    : false;

  // Count valid changes (including edits)
  const hasValidChanges = previewData
    ? previewData.cards_with_new_values > 0 || Object.keys(editedValues).length > 0
    : false;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={getTitle()}
      className={step === 'preview' ? '!max-w-4xl' : '!max-w-lg'}
      disableBackdropClick={isUploading || isSaving}
    >
      <div className="flex flex-col flex-1 min-h-0">
        {/* Content area - not scrollable */}
        <div className="flex-1 flex flex-col space-y-6 min-h-0">
          {/* Period info */}
          <div className="text-sm text-content-subtle flex-shrink-0">
            <span className="font-medium">Period:</span> {periodDisplay} <br />
            <span className="font-medium">Dashboard:</span> {nodeName}
          </div>

          {/* Step content */}
          {step === 'download' && renderDownloadStep()}
          {step === 'upload' && renderUploadStep()}
          {step === 'preview' && renderPreviewStep()}
        </div>

        {/* Footer - always visible */}
        <div className="flex justify-between items-center gap-2 pt-4 mt-4 border-t border-border-default flex-shrink-0">
          {step === 'upload' ? (
            <Button variant="outline" onClick={() => setStep('download')} icon={CaretLeftIcon}>
              Back
            </Button>
          ) : step === 'preview' ? (
            <Button
              variant="outline"
              icon={CaretLeftIcon}
              onClick={() => {
                setPreviewData(null);
                setStep('upload');
              }}
            >
              Back
            </Button>
          ) : (
            <div />
          )}
          {step === 'download' ? (
            <Button variant="outline" onClick={() => setStep('upload')}>
              Upload data
            </Button>
          ) : step === 'preview' && previewData ? (
            <Button
              variant="primary"
              onClick={handleApplyImport}
              disabled={isSaving || hasValidationErrors || !hasValidChanges}
              loading={isSaving}
            >
              Save
            </Button>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
